/**
 * Unified Markdown Conversion Utility
 *
 * This module provides a simplified, single-library approach to markdown conversion
 * using `marked` for its excellent output quality, performance, and simplicity.
 *
 * WHY MARKED:
 * - Clean, standards-compliant HTML output (similar to markdowntohtml.com)
 * - Excellent GitHub Flavored Markdown support
 * - Fast performance and small bundle size
 * - Most widely adopted (11M+ weekly downloads)
 * - Simpler than unified/remark AST approach
 *
 * This replaces the previous remark-based AST approach with direct HTML generation.
 */

import { marked } from "marked";

// Configure marked with GFM (GitHub Flavored Markdown) support
marked.use({
  gfm: true, // Enable GitHub Flavored Markdown
  breaks: false, // Don't convert \n to <br> (standard markdown behavior)
  pedantic: false, // Use modern markdown spec, not original markdown.pl quirks
  mangle: false, // Don't mangle email addresses
} as any);

/**
 * Convert markdown to HTML
 *
 * Simple, fast conversion using marked library.
 * Produces clean, standards-compliant HTML output.
 *
 * @param markdown - The markdown string to convert
 * @returns HTML string
 *
 * @example
 * ```typescript
 * const html = markdownToHtml('# Hello\n\nThis is **bold** text');
 * // Returns: '<h1>Hello</h1>\n<p>This is <strong>bold</strong> text</p>\n'
 * ```
 */
export function markdownToHtml(markdown: string): string {
  return marked.parse(markdown) as string;
}

/**
 * Convert markdown to HTML asynchronously
 *
 * Use this for large markdown documents or when you need async processing.
 *
 * @param markdown - The markdown string to convert
 * @returns Promise resolving to HTML string
 */
export async function markdownToHtmlAsync(markdown: string): Promise<string> {
  return await marked.parse(markdown);
}

/**
 * Parse markdown and extract plain text (strips all HTML tags)
 *
 * Useful for getting text-only version for summaries, search indexing, etc.
 *
 * Uses DOMParser for safe HTML parsing and entity decoding.
 * This avoids security vulnerabilities from regex-based tag stripping
 * and manual entity replacement.
 *
 * @param markdown - The markdown string to parse
 * @returns Plain text with HTML tags removed
 */
export function markdownToPlainText(markdown: string): string {
  const html = marked.parse(markdown) as string;

  // Use DOMParser for safe HTML parsing and text extraction
  // This handles all HTML entities correctly and avoids security issues
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Extract text content (automatically handles all entity decoding safely)
  return (doc.body.textContent || "").trim();
}

/**
 * Check if markdown contains specific elements
 *
 * Useful for conditional rendering or feature detection.
 *
 * @param markdown - The markdown string to analyze
 * @returns Object with boolean flags for each element type
 */
export function analyzeMarkdown(markdown: string): {
  hasHeaders: boolean;
  hasTables: boolean;
  hasCode: boolean;
  hasLists: boolean;
  hasLinks: boolean;
  hasImages: boolean;
} {
  return {
    hasHeaders: /^#{1,6}\s/m.test(markdown),
    hasTables: /^\|.+\|$/m.test(markdown),
    hasCode: /```[\s\S]*?```|`[^`]+`/.test(markdown),
    hasLists: /^[\s]*[-*+]\s|^[\s]*\d+\.\s/m.test(markdown),
    hasLinks: /\[.+\]\(.+\)/.test(markdown),
    hasImages: /!\[.+\]\(.+\)/.test(markdown),
  };
}

/**
 * Custom marked renderer for special use cases
 *
 * Example: Add custom classes to tables, code blocks, etc.
 *
 * @example
 * ```typescript
 * const renderer = {
 *   table(header: string, body: string) {
 *     return `<table class="custom-table">${header}${body}</table>`;
 *   }
 * };
 *
 * const html = markdownToHtmlWithRenderer(markdown, renderer);
 * ```
 */
export function markdownToHtmlWithRenderer(
  markdown: string,
  renderer: Record<string, any>,
): string {
  marked.use({ renderer } as any);

  return marked.parse(markdown) as string;
}
